import { type InferSelectModel } from 'drizzle-orm';
import { type Ref_Crafts, type Ref_Shifts } from '@/src/db/models/MainSchema';

export type AccountRequestCraft = InferSelectModel<typeof Ref_Crafts>;
export type AccountRequestShift = InferSelectModel<typeof Ref_Shifts>;

/**
 * Role the requester will hold on the job. Mirrors the job-site roles in Ref_Roles
 * (field, then managers, then office) so admins can pick the matching role on approval.
 * Fixed order, not A-Z; "Other" is the catch-all.
 */
export const ACCOUNT_REQUEST_ROLES = [
    { ID: 1, Label: 'Foreman' },
    { ID: 2, Label: 'General Foreman' },
    { ID: 3, Label: 'Superintendent' },
    { ID: 4, Label: 'Site Manager' },
    { ID: 5, Label: 'Area Manager' },
    { ID: 6, Label: 'Construction Manager' },
    { ID: 7, Label: 'Project Manager' },
    { ID: 8, Label: 'Project Services' },
    { ID: 9, Label: 'Other' },
] as const;

export type AccountRequestRole = typeof ACCOUNT_REQUEST_ROLES[number];
export type AccountRequestRoleLabel = AccountRequestRole['Label'];

export function isAccountRequestRoleLabel(value: unknown): value is AccountRequestRoleLabel {
    return ACCOUNT_REQUEST_ROLES.some(role => role.Label === value);
}

export interface AccountRequestOptions {
    crafts: AccountRequestCraft[];
    shifts: AccountRequestShift[];
}

export interface AccountRequestOptionsResponse {
    data?: AccountRequestOptions | AccountRequestCraft[];
    message?: string;
    status?: number;
}

export interface AccountRequestFormData {
    EmployeeId: string;
    Email: string;
    UserName: string;
    FirstName: string;
    MiddleName: string;
    LastName: string;
    NickName: string;
    Supervisor: string;
    Craft: AccountRequestCraft | null;
    Shift: AccountRequestShift | null;
    Role: AccountRequestRole | null;
    DefaultJobID: string;
    Password: string;
}

export interface AccountRequestPayload {
    EmployeeId: string;
    Email: string;
    UserName?: string;
    FirstName: string;
    MiddleName?: string;
    LastName: string;
    NickName?: string;
    Supervisor?: string;
    Craft: AccountRequestCraft;
    Shift: AccountRequestShift;
    Role: AccountRequestRoleLabel;
    DefaultJobID: number;
    Password: string;
}

export function createEmptyAccountRequestFormData(): AccountRequestFormData {
    return {
        EmployeeId: '',
        Email: '',
        UserName: '',
        FirstName: '',
        MiddleName: '',
        LastName: '',
        NickName: '',
        Supervisor: '',
        Craft: null,
        Shift: null,
        Role: null,
        DefaultJobID: '',
        Password: '',
    };
}

export function normalizeAccountRequestOptions(response: AccountRequestOptionsResponse): AccountRequestOptions {
    if (Array.isArray(response.data)) {
        return {
            crafts: response.data,
            shifts: [],
        };
    }

    return {
        crafts: response.data?.crafts ?? [],
        shifts: response.data?.shifts ?? [],
    };
}

export function buildAccountRequestPayload(formData: AccountRequestFormData): AccountRequestPayload {
    if (!formData.Craft || !formData.Shift || !formData.Role) {
        throw new Error('Craft, Shift, and Role are required to submit an account request.');
    }

    const userName = formData.UserName.trim();
    const middleName = formData.MiddleName.trim();
    const nickName = formData.NickName.trim();
    const supervisor = formData.Supervisor.trim();

    return {
        EmployeeId: formData.EmployeeId,
        Email: formData.Email,
        UserName: userName || undefined,
        FirstName: formData.FirstName,
        MiddleName: middleName || undefined,
        LastName: formData.LastName,
        NickName: nickName || undefined,
        Supervisor: supervisor || undefined,
        Craft: formData.Craft,
        Shift: formData.Shift,
        Role: formData.Role.Label,
        DefaultJobID: Number(formData.DefaultJobID),
        Password: formData.Password,
    };
}

export function resolveAccountRequestShiftLabel(
    shift: Pick<AccountRequestShift, 'ID' | 'Label'> | null | undefined,
    shifts: readonly Pick<AccountRequestShift, 'ID' | 'Label'>[],
): string {
    const canonicalShift = shift
        ? shifts.find(option => option.ID === shift.ID)
        : undefined;

    return canonicalShift?.Label || shift?.Label || '';
}
